import { useContext, useEffect, useState } from 'react';
import { differenceInSeconds, formatDistanceToNow } from 'date-fns';
import {
  AuthenticationContext,
  TwilioConversation,
  useGetHeroConversationById,
  useGetTwilioConversationById,
  useVsWebsocket,
} from '@vs-online-only-app/state';

const MAX_ONLINE_DELAY_SECONDS = 3 * 60;

type Props = {
  conversationId?: string;
};

const useConversationShopperStatus = ({ conversationId }: Props) => {
  const [isAway, setIsAway] = useState<boolean>();
  const [lastSeenTimeAgo, setLastSeenTimeAgo] = useState<string>('');
  const { data: heroConversation } = useGetHeroConversationById(conversationId);
  const { data: twilioConversation } = useGetTwilioConversationById(
    heroConversation?.twilioConversationId,
  );
  const { user: associate } = useContext(AuthenticationContext);

  const {
    shopperLastOnlineTimestampEventDictionary,
    listenToShopperLastOnlineTimestampEvents,
  } = useVsWebsocket();

  const currentShopperId = heroConversation?.conversationStarter.id;

  let shopperLastOnlineTimestamp =
    heroConversation?.conversationStarter.lastOnlineTimestamp || 0;
  if (currentShopperId) {
    shopperLastOnlineTimestamp = Math.max(
      shopperLastOnlineTimestampEventDictionary?.[currentShopperId] || 0,
      shopperLastOnlineTimestamp,
    );
  }

  useEffect(() => {
    if (!heroConversation || !associate) return;
    listenToShopperLastOnlineTimestampEvents({
      retailerId: associate.retailer,
      shopperId: heroConversation.conversationStarter.id,
    });
  }, [associate, heroConversation, listenToShopperLastOnlineTimestampEvents]);

  useEffect(() => {
    const updateStatus = () => {
      if (!heroConversation || !twilioConversation) {
        return;
      }
      const isAway =
        differenceInSeconds(
          new Date(),
          new Date(shopperLastOnlineTimestamp * 1000),
        ) > MAX_ONLINE_DELAY_SECONDS;

      setIsAway(isAway);

      if (isAway) {
        setLastSeenTimeAgo(
          formatDistanceToNow(new Date(shopperLastOnlineTimestamp * 1000), {
            addSuffix: true,
          }),
        );
      } else {
        setLastSeenTimeAgo(
          (twilioConversation as TwilioConversation).attributes.customerFocused
            ? 'Online'
            : 'On another tab',
        );
      }
    };

    updateStatus();
    const interval = setInterval(updateStatus, 30 * 1000);
    return () => clearInterval(interval);
  }, [heroConversation, twilioConversation, shopperLastOnlineTimestamp]);

  if (!conversationId) {
    return;
  }

  return { isAway, lastSeenTimeAgo };
};

export default useConversationShopperStatus;
